/* global globalThis */
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Tooltip,
  Grid,
  styled,
} from "@mui/material";
import PropTypes from "prop-types";
import { Plus, Pencil, Download, Upload, History } from "lucide-react";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  setCagFilters,
  resetCagFilters,
} from "../redux/slices/cagMappingsSlice";
import CommonFilters from "../components/common/commonFilters/CommonFilters";
import EditConfigurationModal from "../components/cag/EditConfigurationModal";
import CagStagingEnvironment from "../components/cag/CagStagingEnvironment";
import CagUploadHistoryModal from "../components/cag/CagUploadHistoryModal";
import CommonTable from "../components/common/table/commonTable";
import { createHeaderWithSort } from "../components/common/table/columns";
import {
  getCagMappings,
  updateCagMapping,
  createCagMapping,
  fetchDrugLists,
  exportCagMappings,
} from "../api/cagConfigurations";
import { useLoading } from "../context/LoadingContext";
import ProgramIcon from "../components/dashboard/ProgramIcon";
import CommonSearchBar from "../components/common/searchBar/CommonSearchBar";
import { fetchCarriers, fetchGroups, fetchPrograms } from "../api/filterType";

import { usePermissions } from "../redux/permissions";
import { capitalizeFirstLetter } from "../utils/capitalize";
import { formatDateOnly } from "../utils/formatDateTime";

const isNarrowViewport = () =>
  globalThis.window !== undefined && globalThis.window.innerWidth < 2310;

const activeLivesHeader = (
  <span className="cag-lives-header-text">Active lives</span>
);
const startDateHeader = (
  <span className="cag-start-date-header-text">Group start date</span>
);
const endDateHeader = (
  <span className="cag-end-date-header-text">Group end date</span>
);
const useCagFilters = () => {
  const dispatch = useDispatch();
  const filterState = useSelector((state) => state.cagMappingsFilters);

  const {
    search,
    carrierNames,
    groupNames,
    selectedPrograms,
    selectedDrugLists,
    carrierSearch,
    groupSearch,
    programSearch,
    druglistSearch,
    availableCarriers,
    availableGroups,
    availablePrograms,
    availableDrugLists,
    carrierToGroupsMap,
    groupToCarrierMap,
    programNameToIdMap,
    drugListNameToIdMap,
    sorting,
  } = filterState;

  const updateFilter = useCallback(
    (key, valOrFn) => {
      dispatch(
        setCagFilters({
          [key]:
            typeof valOrFn === "function" ? valOrFn(filterState[key]) : valOrFn,
        }),
      );
    },
    [dispatch, filterState],
  );

  const setSearch = useCallback(
    (val) => updateFilter("search", val),
    [updateFilter],
  );
  const setCarrierNames = useCallback(
    (val) => updateFilter("carrierNames", val),
    [updateFilter],
  );
  const setGroupNames = useCallback(
    (val) => updateFilter("groupNames", val),
    [updateFilter],
  );
  const setSelectedPrograms = useCallback(
    (val) => updateFilter("selectedPrograms", val),
    [updateFilter],
  );
  const setSelectedDrugLists = useCallback(
    (val) => updateFilter("selectedDrugLists", val),
    [updateFilter],
  );
  const setSorting = useCallback(
    (val) => updateFilter("sorting", val),
    [updateFilter],
  );
  const setCarrierSearch = useCallback(
    (val) => updateFilter("carrierSearch", val),
    [updateFilter],
  );
  const setGroupSearch = useCallback(
    (val) => updateFilter("groupSearch", val),
    [updateFilter],
  );
  const setProgramSearch = useCallback(
    (val) => updateFilter("programSearch", val),
    [updateFilter],
  );
  const setDruglistSearch = useCallback(
    (val) => updateFilter("druglistSearch", val),
    [updateFilter],
  );
  const setAvailableCarriers = useCallback(
    (val) => updateFilter("availableCarriers", val),
    [updateFilter],
  );
  const setAvailableGroups = useCallback(
    (val) => updateFilter("availableGroups", val),
    [updateFilter],
  );
  const setAvailablePrograms = useCallback(
    (val) => updateFilter("availablePrograms", val),
    [updateFilter],
  );
  const setAvailableDrugLists = useCallback(
    (val) => updateFilter("availableDrugLists", val),
    [updateFilter],
  );
  const setCarrierToGroupsMap = useCallback(
    (val) => updateFilter("carrierToGroupsMap", val),
    [updateFilter],
  );
  const setGroupToCarrierMap = useCallback(
    (val) => updateFilter("groupToCarrierMap", val),
    [updateFilter],
  );
  const setProgramNameToIdMap = useCallback(
    (val) => updateFilter("programNameToIdMap", val),
    [updateFilter],
  );
  const setDrugListNameToIdMap = useCallback(
    (val) => updateFilter("drugListNameToIdMap", val),
    [updateFilter],
  );

  const [loadingMap, setLoadingMap] = useState({});
  const [resetCounter, setResetCounter] = useState(0);

  const handleOpenDropdown = useCallback(
    async (type) => {
      const alreadyHasData =
        (type === "CARRIER" && availableCarriers.length > 0) ||
        (type === "GROUP" && availableGroups.length > 0) ||
        (type === "PROGRAM" && availablePrograms.length > 0) ||
        (type === "DRUG_LIST" && availableDrugLists.length > 0);

      if (alreadyHasData) return;

      setLoadingMap((prev) => ({ ...prev, [type]: true }));
      try {
        const params = {
          outreachType: "MEMBER_OUTREACH",
          source: "CAG_SCREEN",
        };
        if (type === "CARRIER" || type === "GROUP") {
          // Fetch both to establish carrier-group mapping
          const [carrierRes, groupRes] = await Promise.all([
            fetchCarriers(params),
            fetchGroups(params),
          ]);

          const cNames = carrierRes.map(
            (c) => `${c.carrierName} (${c.carrierCode})`,
          );
          const gNames = groupRes.map(
            (g) => `${g.groupName} (${g.groupNumber})`,
          );

          setAvailableCarriers(cNames);
          setAvailableGroups(gNames);

          const c2g = {};
          const g2c = {};
          groupRes.forEach((g) => {
            const cKey = `${g.carrierName} (${g.carrierCode})`;
            const gKey = `${g.groupName} (${g.groupNumber})`;
            if (!c2g[cKey]) c2g[cKey] = [];
            c2g[cKey].push(gKey);
            g2c[gKey] = cKey;
          });
          setCarrierToGroupsMap(c2g);
          setGroupToCarrierMap(g2c);
        } else if (type === "PROGRAM") {
          const res = await fetchPrograms(params);
          setAvailablePrograms(res.map((p) => p.programName));
          const pMap = {};
          res.forEach((p) => {
            pMap[p.programName] = p.programId || p.id;
          });
          setProgramNameToIdMap((prev) => ({ ...prev, ...pMap }));
        } else if (type === "DRUG_LIST") {
          const res = await fetchDrugLists();
          const raw = res?.data?.content || res?.data?.data || res?.data || [];
          const list = Array.isArray(raw) ? raw : [];
          const dlMap = {};
          const dlNames = list
            .map((d) => {
              if (typeof d === "string") return d;
              const name =
                d?.drugListName || d?.name || d?.drug_list_name || "";
              const id = d?.id || d?.drugListId || d?.drug_list_id;
              if (name && id) dlMap[name] = id;
              return name;
            })
            .filter(Boolean);
          setDrugListNameToIdMap((prev) => ({ ...prev, ...dlMap }));
          setAvailableDrugLists(dlNames);
        }
      } catch (err) {
        console.error("Error opening dropdown", type, err);
      } finally {
        setLoadingMap((prev) => ({ ...prev, [type]: false }));
      }
    },
    [
      availableCarriers.length,
      availableGroups.length,
      availablePrograms.length,
      availableDrugLists.length,
      setAvailableCarriers,
      setAvailableGroups,
      setAvailablePrograms,
      setAvailableDrugLists,
      setCarrierToGroupsMap,
      setGroupToCarrierMap,
      setProgramNameToIdMap,
      setDrugListNameToIdMap,
    ],
  );

  return useMemo(
    () => ({
      search,
      setSearch,
      carrierSearch,
      setCarrierSearch,
      groupSearch,
      setGroupSearch,
      programSearch,
      setProgramSearch,
      druglistSearch,
      setDruglistSearch,
      carrierNames,
      setCarrierNames,
      groupNames,
      setGroupNames,
      selectedPrograms,
      setSelectedPrograms,
      selectedDrugLists,
      sorting,
      setSelectedDrugLists,
      setSorting,
      availableCarriers,
      availableGroups,
      availablePrograms,
      availableDrugLists,
      carrierToGroupsMap,
      groupToCarrierMap,
      programNameToIdMap,
      drugListNameToIdMap,
      loadingMap,
      handleOpenDropdown,
      resetCounter,
      setResetCounter,
    }),
    [
      search,
      setSearch,
      carrierSearch,
      setCarrierSearch,
      groupSearch,
      setGroupSearch,
      programSearch,
      setProgramSearch,
      druglistSearch,
      setDruglistSearch,
      carrierNames,
      setCarrierNames,
      groupNames,
      setGroupNames,
      selectedPrograms,
      setSelectedPrograms,
      selectedDrugLists,
      sorting,
      setSelectedDrugLists,
      setSorting,
      availableCarriers,
      availableGroups,
      availablePrograms,
      availableDrugLists,
      carrierToGroupsMap,
      groupToCarrierMap,
      programNameToIdMap,
      drugListNameToIdMap,
      loadingMap,
      handleOpenDropdown,
      resetCounter,
      setResetCounter,
    ],
  );
};

const useCagData = ({
  search,
  carrierNames,
  groupNames,
  selectedPrograms,
  selectedDrugLists,
  sorting,
  pageSize,
  showLoading,
  hideLoading,
  programMapRef,
  drugListMapRef,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const isFetchingMoreRef = useRef(false);
  const isFetching = useRef(false);
  const filterChangeRef = useRef(false);
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (pageNum = 0, isInitial = true, force = false) => {
      setLoading(true);
      if (pageNum === 0 && isInitial) showLoading();
      if (pageNum > 0) isFetchingMoreRef.current = true;

      try {
        const params = buildFetchParams({
          search,
          sorting,
          carrierNames,
          groupNames,
          selectedPrograms,
          selectedDrugLists,
          programMap: programMapRef.current,
          drugListMap: drugListMapRef.current,
        });

        const response = await getCagMappings(params, pageNum, pageSize, false);
        const content = (response?.data?.content || []).map((item) => ({
          ...item.cag,
          id: item.cag?.id,
        }));

        setTotalElements(response?.data?.totalElements || 0);

        if (pageNum === 0) {
          setData(content);
        } else {
          setData((prev) => [...prev, ...content]);
        }
      } catch (error) {
        console.error("API failure:", error);
      } finally {
        setLoading(false);
        isFetchingMoreRef.current = false;
        if (pageNum === 0 && isInitial) hideLoading();
      }
    },
    [
      search,
      carrierNames,
      groupNames,
      selectedPrograms,
      selectedDrugLists,
      sorting,
      showLoading,
      hideLoading,
      pageSize,
      programMapRef,
      drugListMapRef,
    ],
  );

  useEffect(() => {
    setPage(0);
    setData([]);
    filterChangeRef.current = true;
    if (tableRef.current?.scrollElement) {
      tableRef.current.scrollElement.scrollTop = 0;
    }
  }, [
    search,
    carrierNames,
    groupNames,
    selectedPrograms,
    selectedDrugLists,
    sorting,
  ]);

  useEffect(() => {
    fetchData(page, page === 0);
  }, [page, fetchData]);

  const loadMoreItems = useCallback(() => {
    if (loading || isFetching.current || isFetchingMoreRef.current)
      return Promise.resolve();
    setPage((prev) => prev + 1);
    return Promise.resolve();
  }, [loading]);

  return useMemo(
    () => ({
      data,
      loading,
      totalElements,
      page,
      setPage,
      fetchData,
      loadMoreItems,
      tableRef,
      filterChangeRef,
      isFetchingMoreRef,
    }),
    [
      data,
      loading,
      totalElements,
      page,
      fetchData,
      loadMoreItems,
      tableRef,
      filterChangeRef,
      isFetchingMoreRef,
    ],
  );
};

const ResponsiveTooltip = ({
  title,
  children,
  fontSize,
  fontWeight,
  color,
}) => (
  <Tooltip
    title={title || "-"}
    placement="bottom-start"
    arrow
    describeChild
    disableInteractive
    slotProps={{
      popper: {
        sx: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: "#343435",
            color: "#FFFFFF",
            margin: 0,
          },
          "& .MuiTooltip-arrow": {
            color: "#343435",
            margin: 0,
            left: "50% !important",
            transform: "translateX(-50%) !important",
          },
        },
      },
      tooltip: {
        sx: {
          margin: 0,
          marginTop: "0px !important",
          padding: "4px 8px",
        },
      },
    }}
  >
    <div
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: fontWeight || 500,
        lineHeight: "150%",
        fontSize: fontSize || "clamp(14px, 1.1vw, 16px)",
        letterSpacing: "0%",
        color: color || "#10100F",
        width: "100%",
        cursor: "pointer",
      }}
    >
      {children}
    </div>
  </Tooltip>
);

ResponsiveTooltip.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.number,
  color: PropTypes.string,
};

const OrgCell = ({ row }) => {
  const narrow = isNarrowViewport();
  let width = "auto";
  let maxWidth = "none";

  if (!narrow) {
    width = "180px";
    maxWidth = "180px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1430) {
    width = "100px";
    maxWidth = "100px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1600) {
    width = "150px";
    maxWidth = "150px";
  }

  return (
    <Box className="cag-cell" sx={{ width, maxWidth }}>
      <ResponsiveTooltip title={capitalizeFirstLetter(row.original.org)}>
        {capitalizeFirstLetter(row.original.org) || "-"}
      </ResponsiveTooltip>
    </Box>
  );
};

OrgCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const CarrierCell = ({ row }) => {
  const narrow = isNarrowViewport();
  let width = "auto";
  let maxWidth = "none";

  if (!narrow) {
    width = "350px";
    maxWidth = "350px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1430) {
    width = "180px";
    maxWidth = "180px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1600) {
    width = "250px";
    maxWidth = "250px";
  }

  return (
    <Box className="cag-cell" sx={{ width, maxWidth }}>
      <ResponsiveTooltip title={capitalizeFirstLetter(row.original.carrier)}>
        {capitalizeFirstLetter(row.original.carrier) || "-"}
      </ResponsiveTooltip>
      <ResponsiveTooltip
        title={row.original.carrier_code}
        fontSize="clamp(12px, 1vw, 14px)"
        fontWeight={400}
      >
        {row.original.carrier_code || "-"}
      </ResponsiveTooltip>
    </Box>
  );
};

CarrierCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const GroupCell = ({ row }) => {
  const narrow = isNarrowViewport();
  let width = "auto";
  let maxWidth = "none";

  if (!narrow) {
    width = "250px";
    maxWidth = "300px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1430) {
    width = "200px";
    maxWidth = "200px";
  } else if (globalThis !== undefined && globalThis.innerWidth < 1600) {
    width = "300px";
    maxWidth = "300px";
  }

  return (
    <Box className="cag-cell" sx={{ width, maxWidth }}>
      <ResponsiveTooltip title={capitalizeFirstLetter(row.original.group_name)}>
        {capitalizeFirstLetter(row.original.group_name) || "-"}
      </ResponsiveTooltip>
      <ResponsiveTooltip
        title={row.original.group_number}
        fontSize="clamp(12px, 1vw, 14px)"
        fontWeight={400}
      >
        {row.original.group_number || "-"}
      </ResponsiveTooltip>
    </Box>
  );
};

GroupCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const ProgramsCell = ({ row, onDetail }) => {
  const activeProgs = (row.original.programs || []).filter(
    (p) => p.status === "YES",
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        maxHeight: "64px",
        overflow: "hidden",
        width: "100%",
      }}
    >
      {activeProgs.slice(0, 3).map((p) => (
        <Tooltip key={p.name} title={p.name} arrow>
          <Chip
            icon={
              <ProgramIcon
                name={p.name}
                iconColor="#403C6F"
                customStyle={{
                  width: "18px",
                  height: "16px",
                  marginLeft: 8,
                  marginRight: 0,
                }}
              />
            }
            label={p.name}
            size="small"
            sx={{
              height: "26px !important",
              fontSize: "13px",
              fontWeight: 500,
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              borderRadius: "94px",
              maxWidth: "calc(50% - 4px)",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              },
            }}
          />
        </Tooltip>
      ))}
      {activeProgs.length > 3 && (
        <Chip
          label={`+${activeProgs.length - 3}`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDetail(row.original);
          }}
          sx={{
            height: "24px",
            fontSize: "10.5px",
            fontWeight: 600,
            borderRadius: "4px",
            cursor: "pointer",
            bgcolor: "rgba(64, 60, 111, 0.05)",
            color: "#403C6F",
            "&:hover": { bgcolor: "rgba(64, 60, 111, 0.1)" },
          }}
        />
      )}
    </Box>
  );
};

ProgramsCell.propTypes = {
  row: PropTypes.object.isRequired,
  onDetail: PropTypes.func.isRequired,
};

const DrugListCell = ({ row, onDetail }) => {
  const dls = (row.original.programs || []).flatMap((p) => p.drugLists || []);
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        maxHeight: "64px",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {dls.slice(0, 2).map((dl, idx) => (
        <Tooltip key={`${dl.label}-${idx}`} title={dl.label} arrow>
          <Chip
            label={
              dl.label.length > 10
                ? `${dl.label.substring(0, 10)}...`
                : dl.label
            }
            size="small"
            sx={{
              height: "24px",
              fontSize: "13px",
              fontWeight: 500,
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              borderRadius: "4px",
              maxWidth: "140px",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              },
            }}
          />
        </Tooltip>
      ))}
      {dls.length > 2 && (
        <Chip
          label={`+${dls.length - 2}`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDetail(row.original);
          }}
          sx={{
            height: "22px",
            fontSize: "10.5px",
            fontWeight: 600,
            borderRadius: "4px",
            cursor: "pointer",
            bgcolor: "rgba(64, 60, 111, 0.05)",
            color: "#403C6F",
            border: "none",
            "&:hover": { bgcolor: "rgba(64, 60, 111, 0.1)" },
          }}
        />
      )}
    </Box>
  );
};

DrugListCell.propTypes = {
  row: PropTypes.object.isRequired,
  onDetail: PropTypes.func.isRequired,
};

const LivesCell = ({ row }) => (
  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#10100F" }}>
    {row.original.lives || 0}
  </Typography>
);

LivesCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const GroupStartDateCell = ({ row }) => (
  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#10100F" }}>
    {formatDateOnly(row.original.group_start_date) || "-"}
  </Typography>
);

GroupStartDateCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const GroupEndDateCell = ({ row }) => (
  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#10100F" }}>
    {formatDateOnly(row.original.group_end_date) || "-"}
  </Typography>
);

GroupEndDateCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const DateInfoSection = ({ value, label, isMobile }) => (
  <Grid item xs={6} sm={6} md={1.1}>
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRight: isMobile ? "none" : "1px solid #E8E8E9",
        borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          color: "#403C6F",
          mt: isMobile ? 0 : 2,
          mb: 1,
        }}
      >
        {formatDateOnly(value) || "-"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          color: "#6E6D92",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Grid>
);

DateInfoSection.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
};

const ActionsCell = ({ row, onEdit, canWrite }) => {
  return (
    <IconButton
      size="small"
      onClick={() => onEdit(row.original)}
      disabled={!canWrite}
      sx={{
        border: "1px solid #E9E9E8",
        borderRadius: "8px",
        p: "8px",
        opacity: canWrite ? 1 : 0.5,
      }}
    >
      <Pencil size={14} color={canWrite ? "#10100F" : "#92A0B3"} />
    </IconButton>
  );
};

ActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  canWrite: PropTypes.func.isRequired,
};

/**
 * Estimates the diagonal screen size in inches based on screen resolution and DPI
 * @returns {number} Diagonal screen size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.window.screen;
  const dpi = globalThis.window.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const HoverBox = styled(Box)(({ theme }) => ({
  height: "75px",
  width: "100%",
  borderBottom: "2px solid #E8E8E9",
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: "#F9FAFB",
  },
  [theme.breakpoints.down("md")]: {
    height: "auto",
    padding: "16px 0",
  },
}));

const formatOption = (opt) => {
  if (typeof opt !== "string" || !opt.endsWith(")")) return opt;
  const openIndex = opt.lastIndexOf("(");
  if (openIndex === -1) return opt;
  const inner = opt.slice(openIndex + 1, -1);
  if (inner.includes(")")) return opt;
  return opt.slice(0, openIndex).trimEnd();
};

const getNextGroupsOnCarrierChange = (
  newVal,
  carrierNames,
  groupNames,
  carrierToGroupsMap,
) => {
  const added = newVal.filter((c) => !carrierNames.includes(c));
  const removed = carrierNames.filter((c) => !newVal.includes(c));

  let nextGroups = [...groupNames];

  added.forEach((c) => {
    const groups = carrierToGroupsMap[c] || [];
    groups.forEach((g) => {
      if (!nextGroups.includes(g)) {
        nextGroups.push(g);
      }
    });
  });

  removed.forEach((c) => {
    const groups = carrierToGroupsMap[c] || [];
    nextGroups = nextGroups.filter((g) => !groups.includes(g));
  });

  return nextGroups;
};

const getCarriersFromGroups = (newVal, groupToCarrierMap) => {
  const cSet = new Set();
  newVal.forEach((g) => {
    const c = groupToCarrierMap[g];
    if (c) cSet.add(c);
  });
  return Array.from(cSet);
};

const extractCode = (str) => {
  if (typeof str !== "string" || !str.endsWith(")")) return str;
  const openIndex = str.lastIndexOf("(");
  if (openIndex === -1) return str;
  const inner = str.slice(openIndex + 1, -1);
  if (inner.length === 0 || inner.includes(")")) return str;
  return inner;
};

const buildFetchParams = ({
  search,
  sorting,
  carrierNames,
  groupNames,
  selectedPrograms,
  selectedDrugLists,
  programMap,
  drugListMap,
}) => {
  const sortingObj = sorting[0];
  const isDescending = sortingObj?.desc || false;
  let sortDir = "";
  if (sortingObj) {
    sortDir = isDescending ? "desc" : "asc";
  }

  return {
    search,
    sortBy: sortingObj?.id || "",
    sortDirection: sortDir,
    carrierCodes: carrierNames.map(extractCode),
    groupNumbers: groupNames.map(extractCode),
    programIds: selectedPrograms
      .map((p) => Number.parseInt(programMap[formatOption(p)], 10))
      .filter((id) => !Number.isNaN(id)),
    drugListIds: selectedDrugLists
      .map((d) => Number.parseInt(drugListMap[formatOption(d)], 10))
      .filter((id) => !Number.isNaN(id)),
  };
};

const sizeByViewport = (narrow, narrowValue, wideValue) =>
  narrow ? narrowValue : wideValue;

const buildColumnSizing = (
  narrow,
  { narrowSize, wideSize, narrowMin, wideMin, narrowMax, wideMax },
) => ({
  size: sizeByViewport(narrow, narrowSize, wideSize),
  minSize: sizeByViewport(narrow, narrowMin, wideMin),
  maxSize: sizeByViewport(narrow, narrowMax, wideMax),
});

const generateColumns = (setDetailRow, handleEdit, canWrite) => {
  const narrow = isNarrowViewport();

  return [
    {
      header: createHeaderWithSort("Org", true),
      accessorKey: "org",
      id: "org",
      ...buildColumnSizing(narrow, {
        narrowSize: 80,
        wideSize: 180,
        narrowMin: 70,
        wideMin: 150,
        narrowMax: 120,
        wideMax: 250,
      }),
      cell: OrgCell,
    },
    {
      header: createHeaderWithSort("Carrier", true),
      accessorKey: "carrier",
      id: "carrier",
      ...buildColumnSizing(narrow, {
        narrowSize: 200,
        wideSize: 350,
        narrowMin: 180,
        wideMin: 300,
        narrowMax: 250,
        wideMax: 450,
      }),
      cell: CarrierCell,
    },
    {
      header: createHeaderWithSort("Group", true),
      accessorKey: "group_name",
      id: "group",
      ...buildColumnSizing(narrow, {
        narrowSize: 260,
        wideSize: 300,
        narrowMin: 240,
        wideMin: 200,
        narrowMax: 350,
        wideMax: 0,
      }),
      cell: GroupCell,
    },
    {
      header: createHeaderWithSort(startDateHeader, true),
      accessorKey: "group_start_date",
      id: "groupStartDate",
      ...buildColumnSizing(narrow, {
        narrowSize: 130,
        wideSize: 220,
        narrowMin: 110,
        wideMin: 180,
        narrowMax: 150,
        wideMax: 250,
      }),
      cell: GroupStartDateCell,
    },
    {
      header: createHeaderWithSort(endDateHeader, true),
      accessorKey: "group_end_date",
      id: "groupEndDate",
      ...buildColumnSizing(narrow, {
        narrowSize: 130,
        wideSize: 220,
        narrowMin: 110,
        wideMin: 180,
        narrowMax: 150,
        wideMax: 250,
      }),
      cell: GroupEndDateCell,
    },
    {
      header: createHeaderWithSort("Programs", true),
      accessorKey: "programs",
      id: "programs",
      ...buildColumnSizing(narrow, {
        narrowSize: 240,
        wideSize: 320,
        narrowMin: 200,
        wideMin: 280,
        narrowMax: 300,
        wideMax: 450,
      }),
      cell: (props) => <ProgramsCell {...props} onDetail={setDetailRow} />,
    },
    {
      header: createHeaderWithSort("Drug List", true),
      accessorKey: "drugList",
      id: "druglist",
      ...buildColumnSizing(narrow, {
        narrowSize: 210,
        wideSize: 280,
        narrowMin: 180,
        wideMin: 240,
        narrowMax: 260,
        wideMax: 380,
      }),
      cell: (props) => <DrugListCell {...props} onDetail={setDetailRow} />,
    },
    {
      header: createHeaderWithSort(activeLivesHeader, true),
      accessorKey: "lives",
      id: "lives",
      ...buildColumnSizing(narrow, {
        narrowSize: 70,
        wideSize: 110,
        narrowMin: 60,
        wideMin: 90,
        narrowMax: 90,
        wideMax: 150,
      }),
      cell: LivesCell,
    },
    {
      header: "",
      id: "actions",
      accessorKey: "actions",
      size: 60,
      cell: (props) => (
        <ActionsCell {...props} onEdit={handleEdit} canWrite={canWrite} />
      ),
    },
  ];
};

const AccessDenied = () => (
  <Box sx={{ p: 8, textAlign: "center" }}>
    <Typography variant="h5" color="error" gutterBottom>
      Access Denied
    </Typography>
    <Typography color="text.secondary">
      You do not have the required permissions (L6/L7) to access CAG
      Configurations.
    </Typography>
  </Box>
);

const ActionRow = ({
  isMobile,
  search,
  setSearch,
  filtersVisible,
  setFiltersVisible,
  activeFilterCount,
  handleClearFilters,
  canWrite,
  onNewConfig,
  onImportTemplate,
  onVersionHistory,
  onExportExcel,
  exportLoading,
  totalElements,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: isMobile ? "stretch" : "center",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "16px" : "0",
      width: "100%",
      mb: 2,
      mt: 0.7,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "12px" : "16px",
        flex: 1,
      }}
    >
      <Box
        sx={{
          width: isMobile ? "100%" : "280px",
          mr: isMobile ? 0 : "32px",
        }}
      >
        <CommonSearchBar
          onSearch={setSearch}
          value={search}
          placeholder="Search by org, cag, program or drug list..."
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "12px",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <Button
          variant="outlined"
          fullWidth={isMobile}
          onClick={() => setFiltersVisible(!filtersVisible)}
          endIcon={<FilterListRoundedIcon sx={{ fontSize: "16px" }} />}
          sx={{
            height: "35px",
            minWidth: "90px",
            borderRadius: "48px",
            border: "1px solid #E8E8E9",
            padding: "6px 12px 6px 16px",
            textTransform: "none",
            color: "#403C6F",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "0.1px",
            gap: "4px",
            backgroundColor: activeFilterCount === 0 ? "#ECECF1" : "#FFFFFF",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              borderColor: "#403C6F",
              backgroundColor: "rgba(64, 60, 111, 0.04)",
            },
          }}
        >
          {activeFilterCount > 0 && (
            <Box
              sx={{
                minWidth: "22px",
                height: "22px",
                px: activeFilterCount > 9 ? "6px" : 0,
                borderRadius: "12px",
                backgroundColor: "#403C6F",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
                mr: 0.5,
                flexShrink: 0,
              }}
            >
              {activeFilterCount}
            </Box>
          )}
          Filter
        </Button>

        {activeFilterCount > 0 && (
          <Button
            variant="outlined"
            fullWidth={isMobile}
            onClick={handleClearFilters}
            sx={{
              height: "33px",
              minWidth: "70px",
              borderRadius: "48px",
              border: "1px solid #66638C",
              padding: "6px 10px",
              textTransform: "none",
              color: "#403C6F",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              letterSpacing: "0.1px",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                borderColor: "#66638C",
                backgroundColor: "#F7F8FB",
              },
            }}
          >
            Clear Filter
          </Button>
        )}
      </Box>
    </Box>
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        alignItems: "center",
        mt: isMobile ? "16px" : 0,
        width: isMobile ? "100%" : "auto",
      }}
    >
      <Button
        variant="outlined"
        fullWidth={isMobile}
        onClick={onVersionHistory}
        startIcon={<History size={16} />}
        sx={{
          height: "33px",
          minWidth: "140px",
          borderRadius: "48px",
          border: "1px solid #E8E8E9",
          textTransform: "none",
          color: "#403C6F",
          fontSize: "14px",
          fontWeight: 500,
          "&:hover": {
            borderColor: "#403C6F",
            backgroundColor: "rgba(64, 60, 111, 0.04)",
          },
        }}
      >
        Version History
      </Button>
      <Button
        variant="outlined"
        fullWidth={isMobile}
        onClick={onExportExcel}
        disabled={exportLoading || !totalElements || totalElements === 0}
        startIcon={<Download size={16} />}
        sx={{
          height: "33px",
          minWidth: "120px",
          borderRadius: "48px",
          border: "1px solid #E8E8E9",
          textTransform: "none",
          color: "#403C6F",
          fontSize: "14px",
          fontWeight: 500,
          "&:hover": {
            borderColor: "#403C6F",
            backgroundColor: "rgba(64, 60, 111, 0.04)",
          },
          "&:disabled": {
            borderColor: "#E8E8E9",
            color: "#A0A0A0",
          },
        }}
      >
        {exportLoading ? "Exporting..." : "Export"}
      </Button>
      <Button
        variant="outlined"
        fullWidth={isMobile}
        onClick={onImportTemplate}
        disabled={!canWrite}
        startIcon={<Upload size={16} />}
        sx={{
          height: "33px",
          borderRadius: "48px",
          border: "1px solid #E8E8E9",
          textTransform: "none",
          color: "#403C6F",
          fontSize: "14px",
          fontWeight: 500,
          "&:hover": {
            borderColor: "#403C6F",
            backgroundColor: "rgba(64, 60, 111, 0.04)",
          },
          "&:disabled": {
            borderColor: "#E8E8E9",
            color: "#A0A0A0",
          },
        }}
      >
        Import
      </Button>
      <Button
        variant="contained"
        fullWidth={isMobile}
        onClick={onNewConfig}
        disabled={!canWrite}
        sx={{
          bgcolor: "#403C6F",
          color: "white",
          borderRadius: "48px",
          textTransform: "none",
          fontSize: "14px",
          height: "33px",
          "&:disabled": {
            bgcolor: "rgba(64, 60, 111, 0.4)",
            color: "rgba(255, 255, 255, 0.7)",
          },
          "&:hover": { bgcolor: "#34315A" },
        }}
      >
        New Configuration <Plus size={20} style={{ paddingLeft: 4 }} />
      </Button>
    </Box>
  </Box>
);

ActionRow.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  setFiltersVisible: PropTypes.func.isRequired,
  activeFilterCount: PropTypes.number.isRequired,
  handleClearFilters: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
  onNewConfig: PropTypes.func.isRequired,
  onImportTemplate: PropTypes.func,
  onVersionHistory: PropTypes.func,
  onExportExcel: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired,
  totalElements: PropTypes.number.isRequired,
};

const RecordsCount = ({ count }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      mt: 0,
      mb: 1,
      pr: 2,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "#080A0E8A",
        fontWeight: 500,
        fontSize: "14px",
        fontStyle: "italic",
      }}
    >
      Total {count} {count === 1 ? "Record" : "Records"}
    </Typography>
  </Box>
);

RecordsCount.propTypes = {
  count: PropTypes.number,
};

const CagMappingsPage = () => {
  const dispatch = useDispatch();
  const { canRead, canWrite } = usePermissions("CAG Configuration");
  const storedUser = localStorage.getItem("userDetails");
  const parsedUser = storedUser ? JSON.parse(storedUser) : null;
  const userRole = parsedUser?.role || "L7";

  const { showLoading, hideLoading } = useLoading();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStagingOpen, setIsStagingOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [containerScale, setContainerScale] = useState(1);
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const resultsContainerRef = useRef(null);
  const pageSize = 200;

  const filters = useCagFilters();
  const {
    search,
    setSearch,
    carrierNames,
    groupNames,
    selectedPrograms,
    selectedDrugLists,
    programNameToIdMap,
    drugListNameToIdMap,
    resetCounter,
    setResetCounter,
    sorting,
  } = filters;

  const programMapRef = useRef(programNameToIdMap);
  const drugListMapRef = useRef(drugListNameToIdMap);

  useEffect(() => {
    programMapRef.current = programNameToIdMap;
  }, [programNameToIdMap]);

  useEffect(() => {
    drugListMapRef.current = drugListNameToIdMap;
  }, [drugListNameToIdMap]);

  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const params = buildFetchParams({
        search,
        sorting,
        carrierNames,
        groupNames,
        selectedPrograms,
        selectedDrugLists,
        programMap: programMapRef.current,
        drugListMap: drugListMapRef.current,
      });

      await exportCagMappings(params, false);
    } catch (error) {
      console.error("CAG mappings export failed:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const dataFetcher = useCagData({
    ...filters,
    pageSize,
    showLoading,
    hideLoading,
    programMapRef,
    drugListMapRef,
  });

  const { data, loading, totalElements, fetchData, loadMoreItems, tableRef } =
    dataFetcher;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleCarrierChange = useCallback(
    (newVal) => {
      const nextGroups = getNextGroupsOnCarrierChange(
        newVal,
        carrierNames,
        groupNames,
        filters.carrierToGroupsMap,
      );
      filters.setCarrierNames(newVal);
      filters.setGroupNames(nextGroups);
    },
    [carrierNames, groupNames, filters],
  );

  const handleGroupChange = useCallback(
    (newVal) => {
      filters.setGroupNames(newVal);
      filters.setCarrierNames(
        getCarriersFromGroups(newVal, filters.groupToCarrierMap),
      );
    },
    [filters],
  );

  useEffect(() => {
    const inches = getScreenInches();
    setContainerScale(inches < 14 ? 0.9 : 1);
  }, []);

  useEffect(() => {
    if (isStagingOpen) return;
    globalThis.window?.scrollTo?.(0, 0);

    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh =
        globalThis.window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        globalThis.window.innerWidth <= 1440 &&
        globalThis.window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedTableHeight(h);
    };

    computeHeight();
    const rafId = requestAnimationFrame(computeHeight);
    const timer = setTimeout(computeHeight, 100);

    globalThis.window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      if (document.body) ro.observe(document.body);
    } catch (error) {
      console.debug("ResizeObserver loop limit exceeded:", error);
    }
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
      globalThis.window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, [filtersVisible, isStagingOpen]);

  const activeFilterCount = useMemo(() => {
    return (
      (carrierNames?.length || 0) +
      (groupNames?.length || 0) +
      (selectedPrograms?.length || 0) +
      (selectedDrugLists?.length || 0)
    );
  }, [carrierNames, groupNames, selectedPrograms, selectedDrugLists]);

  const handleClearFilters = () => {
    dispatch(resetCagFilters());
    setResetCounter((prev) => prev + 1);
  };

  const handleEdit = useCallback((rowData) => {
    setSelectedRow({ cag: rowData });
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleSave = async (payload) => {
    showLoading();
    try {
      if (selectedRow?.cag?.id) {
        await updateCagMapping(selectedRow.cag.id, payload);
      } else {
        await createCagMapping({
          ...payload,
          cagDetail: { ...payload.cagDetail, pcaDirectiveEnabled: true },
        });
      }
      setSelectedRow(null);
      setIsModalOpen(false);
      await fetchData(0, true, true);
      return { success: true };
    } catch (error) {
      console.error("Error saving CAG mapping:", error);
      return { success: false, error };
    } finally {
      hideLoading();
    }
  };

  const columns = useMemo(
    () => generateColumns(setDetailRow, handleEdit, canWrite),
    [canWrite, handleEdit],
  );

  const isAuthorizedRole = ["L6", "L7"].includes(userRole);

  if (!isAuthorizedRole || !canRead) {
    return <AccessDenied />;
  }

  if (isStagingOpen) {
    return (
      <CagStagingEnvironment
        onBack={() => {
          globalThis.window?.scrollTo?.(0, 0);
          setIsStagingOpen(false);
          fetchData(0, true, true);
        }}
        canWrite={canWrite}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#F7F8FB",
        overflow: "hidden",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          p: "24px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          transform: `scale(${containerScale})`,
          transformOrigin: "top left",
          width: containerScale < 1 ? "111.1%" : "100%",
          height: "100%",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "-8px 0 -4px 0",
            minHeight: "40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "18px",
              fontWeight: 600,
              color: "#080A0E",
              "& .breadcrumb-separator": {
                margin: "0 4px",
                color: "#080A0E",
              },
            }}
          >
            <span style={{ cursor: "default" }}>CAG Configuration</span>
          </Box>
        </Box>

        <ActionRow
          isMobile={isMobile}
          search={search}
          setSearch={setSearch}
          filtersVisible={filtersVisible}
          setFiltersVisible={setFiltersVisible}
          activeFilterCount={activeFilterCount}
          handleClearFilters={handleClearFilters}
          canWrite={canWrite}
          onNewConfig={() => {
            setSelectedRow(null);
            setIsModalOpen(true);
          }}
          onImportTemplate={() => setIsStagingOpen(true)}
          onVersionHistory={() => setIsVersionHistoryOpen(true)}
          onExportExcel={handleExportExcel}
          exportLoading={exportLoading}
          totalElements={totalElements}
        />

        {filtersVisible && (
          <Box sx={{ mt: -1 }}>
            <CommonFilters
              key={`common-filters-${resetCounter}`}
              visibleFilters={["CARRIER", "GROUP", "PROGRAM", "DRUG_LIST"]}
              carrierFilter={carrierNames}
              groupFilter={groupNames}
              programFilter={selectedPrograms}
              druglistFilter={selectedDrugLists}
              onCarrierChange={handleCarrierChange}
              onGroupChange={handleGroupChange}
              onProgramChange={filters.setSelectedPrograms}
              onDruglistChange={filters.setSelectedDrugLists}
              onOpenDropdown={filters.handleOpenDropdown}
              onSearch={(term, type) => {
                if (type === "CARRIER") filters.setCarrierSearch(term);
                else if (type === "GROUP") filters.setGroupSearch(term);
                else if (type === "PROGRAM") filters.setProgramSearch(term);
                else if (type === "DRUG_LIST") filters.setDruglistSearch(term);
              }}
              carrierSearch={filters.carrierSearch}
              groupSearch={filters.groupSearch}
              programSearch={filters.programSearch}
              druglistSearch={filters.druglistSearch}
              loadingMap={filters.loadingMap}
              carriers={filters.availableCarriers}
              groups={filters.availableGroups}
              programs={filters.availablePrograms}
              drugLists={filters.availableDrugLists}
              showLabel={true}
              compact={false}
            />
          </Box>
        )}

        <RecordsCount count={totalElements} />
        <Box
          ref={resultsContainerRef}
          sx={{ flexGrow: 1, minHeight: 400, overflow: "hidden" }}
        >
          <CommonTable
            ref={tableRef}
            columns={columns}
            data={data}
            loading={loading}
            onSortingChange={filters.setSorting}
            sorting={filters.sorting}
            manualSorting={true}
            itemCount={totalElements}
            paginate={false}
            pageSize={pageSize}
            isItemLoaded={(index) => !!data[index]}
            onLoadMoreItems={loadMoreItems}
            tableHeight={
              computedTableHeight ||
              (filtersVisible ? "calc(100vh - 250px)" : "calc(100vh - 190px)")
            }
            useColumnMinWidth={true}
            renderRow={({ row }) => (
              <CagMappingRow
                row={row}
                isMobile={isMobile}
                onDetail={setDetailRow}
                onEdit={handleEdit}
                canWrite={canWrite}
              />
            )}
          />
        </Box>

        <MappingDetailModal
          open={Boolean(detailRow)}
          onClose={() => setDetailRow(null)}
          data={detailRow}
        />

        <EditConfigurationModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          initialData={selectedRow}
          availableCarriers={filters.availableCarriers}
          availableGroups={filters.availableGroups}
          carrierToGroupsMap={filters.carrierToGroupsMap}
        />

        <CagUploadHistoryModal
          open={isVersionHistoryOpen}
          onClose={() => setIsVersionHistoryOpen(false)}
          onResetSuccess={() => {
            fetchData(0, true, true);
          }}
        />
      </Box>
    </Box>
  );
};

const ClientInfoSection = ({ row, isMobile }) => (
  <Grid item xs={12} sm={6} md={1.5}>
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRight: isMobile ? "none" : "1px solid #E8E8E9",
        borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          color: "#403C6F",
        }}
      >
        {capitalizeFirstLetter(row.original.name || row.original.org) || "-"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          color: "#6E6D92",
          textTransform: "uppercase",
        }}
      >
        Client Name
      </Typography>
    </Box>
  </Grid>
);

ClientInfoSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

const CarrierInfoSection = ({ row, isMobile }) => (
  <Grid item xs={12} sm={6} md={2}>
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRight: isMobile ? "none" : "1px solid #E8E8E9",
        borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Tooltip
        title={capitalizeFirstLetter(row.original.carrier) || "-"}
        placement="top"
        arrow
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "1.2",
            overflow: "hidden",
            mt: isMobile ? 0 : 2,
            mb: 1,
          }}
        >
          {capitalizeFirstLetter(row.original.carrier) || "-"}
        </Typography>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          color: "#6E6D92",
          textTransform: "uppercase",
        }}
      >
        Carrier Name
      </Typography>
    </Box>
  </Grid>
);

CarrierInfoSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

const GroupInfoSection = ({ row, isMobile }) => (
  <>
    <Grid item xs={6} sm={6} md={1.5}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderRight: isMobile ? "none" : "1px solid #E8E8E9",
          borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#403C6F",
            mt: isMobile ? 0 : 2,
            mb: 1,
          }}
        >
          {row.original.group_number || "-"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "11px",
            letterSpacing: "0.5px",
            color: "#6E6D92",
            textTransform: "uppercase",
          }}
        >
          Group ID
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={6} sm={6} md={2}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderRight: isMobile ? "none" : "1px solid #E8E8E9",
          borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Tooltip
          title={capitalizeFirstLetter(row.original.group_name) || "-"}
          placement="top"
          arrow
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "1.2",
              overflow: "hidden",
              mt: isMobile ? 0 : 2,
              mb: 1,
            }}
          >
            {capitalizeFirstLetter(row.original.group_name) || "-"}
          </Typography>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            fontSize: "11px",
            letterSpacing: "0.5px",
            color: "#6E6D92",
            textTransform: "uppercase",
          }}
        >
          Group Name
        </Typography>
      </Box>
    </Grid>
  </>
);

GroupInfoSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

const ProgramsSection = ({ row, isMobile, onDetail }) => {
  const activeProgs = (row.original.programs || []).filter(
    (p) => p.status === "YES",
  );
  return (
    <Grid item xs={12} sm={6} md={2.5}>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderRight: isMobile ? "none" : "1px solid #E8E8E9",
          borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            maxHeight: isMobile ? "none" : "34px",
            overflow: isMobile ? "visible" : "hidden",
          }}
        >
          {activeProgs.slice(0, isMobile ? 10 : 2).map((p) => (
            <Tooltip key={p.name} title={p.name} placement="top" arrow>
              <Box sx={{ display: "inline-block" }}>
                <Chip
                  label={p.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: "18px",
                    fontSize: "14px",
                    backgroundColor: "#f0f2f5",
                    maxWidth: "100px",
                    fontWeight: 500,
                    border: "none",
                    mb: 0.5,
                  }}
                />
              </Box>
            </Tooltip>
          ))}
          {!isMobile && activeProgs.length > 2 && (
            <Chip
              label={`+${activeProgs.length - 2}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDetail(row.original);
              }}
              sx={{
                height: "18px",
                fontSize: "10px",
                cursor: "pointer",
                backgroundColor: "#e8eaed",
                mb: 0.5,
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            letterSpacing: "0.5px",
            color: "#6E6D92",
            textTransform: "uppercase",
            mt: 1,
          }}
        >
          Programs
        </Typography>
      </Box>
    </Grid>
  );
};

ProgramsSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  onDetail: PropTypes.func.isRequired,
};

const DrugListsSection = ({ row, isMobile, onDetail }) => {
  const dls = (row.original.programs || []).flatMap((p) => p.drugLists || []);
  return (
    <Grid item xs={12} sm={6} md={1.5}>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderRight: isMobile ? "none" : "1px solid #E8E8E9",
          borderBottom: isMobile ? "1px solid #F0F0F1" : "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            maxHeight: isMobile ? "none" : "34px",
            overflow: isMobile ? "visible" : "hidden",
          }}
        >
          {dls.slice(0, isMobile ? 10 : 2).map((dl) => (
            <Tooltip key={dl.label} title={dl.label} placement="top" arrow>
              <Box sx={{ display: "inline-block" }}>
                <Chip
                  label={dl.label}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "14px",
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    maxWidth: "80px",
                    fontWeight: 500,
                    border: "none",
                    mb: 0.5,
                  }}
                />
              </Box>
            </Tooltip>
          ))}
          {!isMobile && dls.length > 2 && (
            <Chip
              label={`+${dls.length - 2}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDetail(row.original);
              }}
              sx={{
                height: "18px",
                fontSize: "10px",
                cursor: "pointer",
                backgroundColor: "#e8eaed",
                mb: 0.5,
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            letterSpacing: "0.5px",
            color: "#6E6D92",
            textTransform: "uppercase",
            mt: 1,
          }}
        >
          Drug Lists
        </Typography>
      </Box>
    </Grid>
  );
};

DrugListsSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  onDetail: PropTypes.func.isRequired,
};

const LivesSection = ({ row, isMobile, onEdit, canWrite }) => (
  <Grid item xs={12} sm={6} md={1}>
    <Box
      sx={{
        px: 2,
        py: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: isMobile ? "flex-start" : "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            mr: 2,
            color: "#10100F",
          }}
        >
          {row.original.lives || "0"}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row.original);
          }}
          disabled={!canWrite}
          sx={{ padding: "4px", opacity: canWrite ? 1 : 0.5 }}
        >
          <Pencil size={14} color={canWrite ? "#6E6D92" : "#92A0B3"} />
        </IconButton>
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: "10px",
          letterSpacing: "0.5px",
          color: "#6E6D92",
          textTransform: "uppercase",
        }}
      >
        Active lives
      </Typography>
    </Box>
  </Grid>
);

LivesSection.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
};

const CagMappingRow = ({ row, isMobile, onDetail, onEdit, canWrite }) => {
  return (
    <HoverBox
      onClick={(e) => {
        if (e.target.closest("button") || e.target.closest(".MuiChip-root"))
          return;
        onDetail(row.original);
      }}
    >
      <Grid
        container
        alignItems="center"
        sx={{ height: "100%", width: "100%", mx: 0 }}
      >
        <ClientInfoSection row={row} isMobile={isMobile} />
        <CarrierInfoSection row={row} isMobile={isMobile} />
        <GroupInfoSection row={row} isMobile={isMobile} />
        <DateInfoSection
          row={row}
          isMobile={isMobile}
          label="Group Start Date"
          value={row.original.group_start_date}
        />
        <DateInfoSection
          row={row}
          isMobile={isMobile}
          label="Group End Date"
          value={row.original.group_end_date}
        />
        <ProgramsSection row={row} isMobile={isMobile} onDetail={onDetail} />
        <DrugListsSection row={row} isMobile={isMobile} onDetail={onDetail} />
        <LivesSection
          row={row}
          isMobile={isMobile}
          onEdit={onEdit}
          canWrite={canWrite}
        />
      </Grid>
    </HoverBox>
  );
};

CagMappingRow.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  onDetail: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
};

const MappingDetailModal = ({ open, onClose, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!data) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            width: isMobile ? "95%" : "auto",
            minWidth: isMobile ? "95%" : "40%",
            p: isMobile ? "12px" : "20px",
            bgcolor: "#F7F8FB",
            m: isMobile ? 1 : 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          mb: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "18px" : "20px",
            color: "#10100F",
          }}
        >
          Program & Drug List Mapping
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#10100F" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: isMobile ? "60vh" : "70vh",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#403C6F",
                    bgcolor: "#CFCEDA",
                    border: "none",
                    width: "50%",
                    borderRight: "1px solid white",
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  Programs
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#403C6F",
                    bgcolor: "#CFCEDA",
                    border: "none",
                    width: "48%",
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  Drug List
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.programs || []).map((prog) => (
                <TableRow
                  key={prog.name}
                  sx={{
                    height: isMobile ? "auto" : "72px",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <TableCell
                    sx={{
                      borderBottom: "2px solid #F7F8FB",
                      borderRight: "1px solid #F7F8FB",
                      verticalAlign: "middle",
                      p: isMobile ? 1.5 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        px: "12px",
                        py: "6px",
                        borderRadius: "94px",
                      }}
                    >
                      <ProgramIcon name={prog.name} iconColor="#403C6F" />
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px !important",
                          color: "#403C6F",
                        }}
                      >
                        {prog.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "2px solid #F7F8FB",
                      verticalAlign: "middle",
                      p: isMobile ? 1.5 : 2,
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {(prog.drugLists || []).map((dl) => (
                        <Chip
                          key={dl.label}
                          label={dl.label}
                          size="small"
                          sx={{
                            height: "26px",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#403C6F",
                            bgcolor: "rgba(64, 60, 111, 0.05)",
                            borderRadius: "4px",
                          }}
                        />
                      ))}
                      {(!prog.drugLists || prog.drugLists.length === 0) && "-"}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

MappingDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default CagMappingsPage;
